import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';
import { toRaw, watch } from 'vue';

import HaspObject from './HaspObject';
import { range } from '../utils/utils';
import CustomArc from '../draw/CustomArc';
import { processIconText } from '../utils/iconFonts';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { useDatasetStore } from '../stores/datasetStore.js';
import { contentBox, onPaddingStyle } from './properties/v2/padding.js';
import { createContentClipGroup } from './contentClip.js';
import {
    ensureGaugeNeedleIds,
    getGaugeNeedleId,
    getGaugeNeedleRenderPlan,
    normalizeGaugeNeedleValues,
    applyGaugeNeedlesExport,
    getGaugeNeedleBindableProperties,
    getConfiguredGaugeNeedles,
    commitGaugeNeedlePreview,
    discardGaugeNeedlePreview,
    captureGaugeManualState,
    restoreGaugeManualState,
} from './gaugeNeedles.js';
import { formatObjectDecimals } from './objectValueFormatting.js';

// min	            int16	0	minimum value of the indicator
// max	            int16	100	maximum value of the indicator
// val	            int16	0	current value of the indicator
// critical_value	int16	80	scale color will be changed to scale_end_color after this value
// label_count	    uint8		number of labels (and major ticks) of the scale
// line_count	    uint16	31	number of minor ticks of the entire scale
// angle	        0-360	240	angle between start and end of the scale
// rotation	        0-360	0	offset for the gauge's angles to rotate it
// format	        uint16	0	divider for major tick values

// scale_grad_color	color	Gradient to this color on the scale lines, or gauge minor ticks
// scale_end_color	color	Color of the scale lines in the end region, or gauge minor ticks / critical area
// scale_width	    int16	Width of the scale in the normal region, or length of gauge minor ticks
// scale_border_width	int16	Width of a border drawn on the outer side of the scale in the normal region
// scale_end_line_width	int16	Width of a scale line in the end region
// scale_end_border_width	int16	Width of a border drawn on the outer side of the scale in the end region

// Example firmware wire-format payload (main/indicator/major_ticks style
// properties, plus text_font/text_color/value_str/value_font):
// "line_color":"#ffa727","line_width":10,"text_font":43,"text_color":"#ffffff","value_str":"test","value_font":79

export default class HaspGauge extends HaspObject {
    rot_offset = -90;
    div_format = 1;
    centerRadius = 10;

    // hassTemplate = '/media/templates/gauge.ftl';

    constructor(config) {
        super(config);

        // ConfigManager copies schema keys uniformly. Remove the undefined
        // legacy placeholder again so importing `{ obj: 'gauge', val: 50 }`
        // does not create a collection merely because the schema knows about
        // the optional multiple mode.
        if (Array.isArray(this.needles)) ensureGaugeNeedleIds(this.needles);
        else delete this.needles;
        this._captureManualGaugeState();
        this._syncGaugeNeedleProperties();

        this._scaleFontBinding = new FontBinding((family, loaded) => {
            this._scaleFontFamily = family;
            if (loaded) this.drawLines();
        });

        this._valueFontBinding = new FontBinding((family) => {
            this.valueText.fontFamily(family);
            this.valueText.getLayer()?.batchDraw();
        });

        this.on('transform', function () {
            this.drawLines();
            // this.drawNeedle();
            // this.valueText.size(this.rect.size());
            this.textPosition();
        });

        this.on('transformend', function () {
            this.size(this.rect.size());
            this.drawLines();
            // this.drawNeedle();
            // this.valueText.size(this.rect.size());
            this.textPosition();
        });

        this.onStyle('main', 'scale_border_width', () => {
            this.drawLines();
        })

        this.onStyle('main', 'line_color', () => {
            this.drawLines();
            // this.drawNeedle();
        })
        // Shorthand + all four per-side overrides, one subscription helper.
        onPaddingStyle(this, 'main', () => {
            this.drawLines();
            // this.drawNeedle();
        })

        this.onStyle('main', 'line_width', () => {
            this.drawLines();
            // this.drawNeedle();
        })
        this.onStyle('main', 'scale_width', () => {
            this.drawLines();
            // this.drawNeedle();
        })
        this.onStyle('indicator', 'line_width', () => {
            this.drawLines();
            // this.drawNeedle();
        })
        this.onStyle('major_ticks', 'line_width', () => {
            this.drawLines();
            // this.drawNeedle();
        })
        this.onStyle('major_ticks', 'scale_width', () => {
            this.drawLines();
            // this.drawNeedle();
        })
        this.onStyle('indicator', 'line_rounded', () => {
            let cap = 'square';
            if (this.style('indicator').line_rounded) {
                cap = 'round';
            }
            this.needleLines?.forEach(line => line.lineCap(cap));
        })
        //Needle changes
        this.onStyle('indicator', 'bg_color', () => {
            this.needleCenter?.fill(this.style('indicator').bg_color);
        })
        this.onStyle('indicator', 'border_color', () => {
            this.needleCenter?.stroke(this.style('indicator').border_color);
        })
        this.onStyle('indicator', 'border_width', () => {
            // Bug fix: was this.needleCenter.stroke_width(...) — not a real
            // Konva method (should be strokeWidth) — this listener was a
            // silent no-op before.
            this.needleCenter?.strokeWidth(this.style('indicator').border_width);
        })
        this.onStyle('indicator', 'line_color', () => {
            // In multiple mode explicit per-needle colors remain authoritative;
            // the shared indicator color is only the fallback for an item whose
            // color was omitted. Rebuild so that fallback behavior is correct.
            if (Array.isArray(this.needles)) this.redraw();
            else this.needleLines?.forEach(line => line.stroke(this.style('indicator').line_color));
        })
        this.onStyle('indicator', 'line_width', () => {
            this.needleLines?.forEach(line => line.strokeWidth(this.style('indicator').line_width));
        })

        this.on('needles', () => {
            this._syncGaugeNeedleProperties();
            if (!this.dataset) this._captureManualGaugeState();
            this.redraw();
        });

        this.on('data_val', () => {
            if (!this.dataset) this._captureManualGaugeState();
        });

        this.on('dataset', () => {
            if (!this.dataset) this._restoreManualGaugeState();
            this.redraw();
            this.getLayer()?.batchDraw();
        });

        this.on('decimals', () => {
            this.redraw();
            this.getLayer()?.batchDraw();
        });

        this.onStyle('major_ticks', 'line_color', () => {
            this.drawLines();
            // this.drawNeedle();
        })

        this.onStyle('main', 'text_color', () => {
            const { text_color } = this.style('main');
            this.lineGroup.getChildren().forEach((element) => {
                if (element.attrs.obj === 'textobj') {
                    element.fill(text_color);
                }
            });
        });

        this.on('value_color', (e) => {
            this.valueText.fill(this.value_color);
        })

        this.on('value_str', (e) => {
            this.valueText.text(processIconText(this.value_str));
        });

        this.on('value_font', (e) => {
            this.valueText.fontSize(this.value_font);
        });

        this.onStyle('main', 'keep_circle', () => {
            // if (this.keepCircle) this.radius = 999;
            // else this.radius = this.height() / 2;
            this.rect.cornerRadius(this.style('main').radius);
        });

        this.onStyle('main', 'scale_end_color', () => {
            this.drawLines();
        })

        this.onStyle('major_ticks', 'scale_end_color', () => {
            this.drawLines();
        })

        this.on('font', () => {
            this._applyScaleFont();
            this.drawLines();
        });

        this.onStyle('main', 'text_font_family', () => {
            this._applyScaleFont();
            this.drawLines();
        });

        this.on('value_font_resource', () => {
            this._applyValueFont();
        });

        this.on('destroy', () => {
            this._stopDatasetWatch?.();
            this._scaleFontBinding.dispose();
            this._valueFontBinding.dispose();
        });

        this.draggable(true);

        // LVGL draws the complete gauge with the widget draw mask. Keep all
        // generated scale/needle/value visuals in one clipped render group;
        // selection and the Transformer remain on the owner/layer.
        this.contentGroup = createContentClipGroup(this);

        this._applyScaleFont();
        this.redraw()
        this.createText();
        this._applyValueFont();

        this._stopDatasetWatch = watch(
            () => this._gaugeDatasetSignature(),
            () => {
                discardGaugeNeedlePreview(this);
                this.redraw();
                this.getLayer()?.batchDraw();
            },
        );
    }

    /**
     * The uploaded Font Resource `this.font` resolves to for canvas preview
     * (see HaspLabel.js's identically-named helper for the full semantics).
     */
    _fontResource() {
        return this.font ? useFontStore().fonts.find(f => f.file === this.font) : null;
    }

    /**
     * Resolve which font family the scale labels (drawLabel) should render
     * with, mirroring HaspLabel.js's _applyTextFont(). Since drawLines()
     * recreates all label Konva.Text nodes from scratch on every redraw,
     * there's no per-node fontFamily to update in place — this just keeps
     * this._scaleFontFamily current; the FontBinding callback only triggers
     * a redraw itself for the asynchronous "Font Resource finished loading"
     * transition (loaded === true) — the synchronous/fallback stage's redraw
     * is already handled by the callers of _applyScaleFont() below.
     */
    _applyScaleFont() {
        const { text_font_family } = this.style('main');
        this._scaleFontBinding.set(this._fontResource(), text_font_family || 'Roboto Condensed');
    }

    /**
     * The uploaded Font Resource `this.value_font_resource` resolves to for
     * canvas preview — independent resource slot from `this.font`, since
     * firmware binds VALUE_FONT and TEXT_FONT separately (hasp_font_binding.h).
     */
    _valueFontResource() {
        return this.value_font_resource ? useFontStore().fonts.find(f => f.file === this.value_font_resource) : null;
    }

    /**
     * Resolve which font family the value text (this.valueText) should
     * render with. Unlike the scale labels, valueText is a single persistent
     * Konva.Text node, so its fontFamily is updated in place. Falls back to
     * 'Material Icons' (the pre-existing hardcoded default) when no Font
     * Resource is selected.
     */
    _applyValueFont() {
        this._valueFontBinding.set(this._valueFontResource(), 'Material Icons');
    }

    _resolveDataset(datasetUUID) {
        return datasetUUID ? useDatasetStore().getDataset(datasetUUID) : null;
    }

    _gaugeDatasetSignature() {
        if (!this.dataset) return null;
        const dataset = useDatasetStore().getDataset(this.dataset);
        return `${this.dataset}:${JSON.stringify(dataset?.series ?? null)}`;
    }

    _captureManualGaugeState() {
        this._manualGaugeState = captureGaugeManualState(
            this,
            this.style('indicator').line_color,
        );
    }

    _restoreManualGaugeState() {
        restoreGaugeManualState(this, this._manualGaugeState, color => {
            this.setStyle('indicator', 'line_color', color);
        });
    }

    setNewSize(size) {
        super.setNewSize(size);
        this.drawLines();
        this.textPosition();
    }

    _syncGaugeNeedleProperties() {
        const dynamic = getGaugeNeedleBindableProperties(this);
        const regular = (this.hass_properties ?? [])
            .filter(property => !/^needles\[\d+\]\.(val|color)$/.test(property));
        this.hass_properties = [...new Set([...regular, ...dynamic])];

        // Action references selected in the Designer carry a non-wire
        // needleId. Keep their authored property name synchronized with the
        // current array index after a reorder; removed items remain untouched
        // and therefore resolve as unavailable instead of silently retargeting.
        if (!Array.isArray(this.needles)) return;
        ensureGaugeNeedleIds(this.needles);
        const indexById = new Map(this.needles.map((needle, index) => [getGaugeNeedleId(needle), index]));
        const renumbered = action => {
            const reference = action?.property;
            if (action?.type !== 'setObjectProperty' || !reference?.needleId) return null;
            const match = /^needles\[\d+\]\.(val|color)$/.exec(reference.name ?? '');
            const index = match ? indexById.get(reference.needleId) : undefined;
            return index === undefined ? null : `needles[${index}].${match[1]}`;
        };
        (Array.isArray(this.actions) ? this.actions : []).flatMap(handler => handler?.actions ?? []).forEach(action => {
            const name = renumbered(action);
            if (name) action.property.name = name;
        });

        // Event/action nodes on this page reference needles the same way. This
        // is a derived fix-up of the graph, like the one above, not a user edit.
        // Unwrapped at every step: a Konva parent can be a Vue proxy, and its
        // logic must stay plain data.
        let page = toRaw(toRaw(this).getParent?.());
        while (page && page.obj !== 'page' && page.obj !== 'template_page') page = toRaw(page.getParent?.());
        if (!page?.logic) return;
        let changed = false;
        const nodes = page.logic.nodes.map(node => {
            const name = renumbered(node.data);
            if (!name || name === node.data.property.name) return node;
            changed = true;
            return { ...node, data: { ...node.data, property: { ...node.data.property, name } } };
        });
        if (changed) page.logic = { ...page.logic, nodes };
    }

    getConfiguredProperty(property) {
        if (property === 'needles') return getConfiguredGaugeNeedles(this);
        return super.getConfiguredProperty(property);
    }

    drawLines() {
        // console.log('drawlines')
        if (this.lineGroup !== undefined) {
            this.lineGroup.destroy()

        }
        const main = this.style('main');
        const majorTicksStyle = this.style('major_ticks');

        this.labels = new Array();
        this.lines = new Array();
        // Effective content box (per-side padding, see v2/padding.js) — the
        // tick group is offset to its origin and the scale radius comes from
        // its width, exactly as the old scalar `padding` maths did.
        const box = contentBox(main, this.width(), this.height());
        const halfWidth = box.width / 2;

        this.lineGroup = new Konva.Group({
            x: box.x,
            y: box.y,
            listening: false,
        })
        const outerRadius = halfWidth;
        const smallInnerRadius = outerRadius - main.scale_width;
        const majorInnerRadius = outerRadius - majorTicksStyle.scale_width;
        var innerRadius = majorInnerRadius;

        const tickValues = range(this.min, this.max, this.line_count);
        const majorTicks = range(this.min, this.max, this.label_count);
        var angle = 0;

        var linecolor = main.line_color;
        var linewidth = main.line_width;
        const correctionAngle = this.rot_offset + this.hasp_rotation - (this.hasp_angle / 2);
        // var criticatStartAngle = 0;
        //Minor ticks
        tickValues.forEach(i => {
            angle = (((i - this.min) / (this.max - this.min)) * this.hasp_angle) + correctionAngle;
            if (i >= this.critical_value) {
                linecolor = majorTicksStyle.scale_end_color;
                linewidth = 3;
                // if (criticatStartAngle === 0) criticatStartAngle = angle;
            }
            const tick = this.drawTick(angle, halfWidth, halfWidth, smallInnerRadius, outerRadius, linewidth, linecolor);
            if (tick) {
                this.lines.push(tick);
            }
        });

        if (tickValues.length > 1 && this.critical_value > this.min) {
            var criticalangle = (((this.critical_value - this.min) / (this.max - this.min)) * this.hasp_angle) + correctionAngle;
            this.drawCriticalArc(angle - criticalangle, criticalangle, halfWidth, halfWidth, outerRadius, main.scale_end_color)
        }

        this.drawScaleBorder(this.hasp_angle - (angle - criticalangle), correctionAngle, halfWidth, halfWidth, outerRadius, main.line_color)


        linecolor = majorTicksStyle.line_color;
        var linewidth = majorTicksStyle.line_width;

        //major ticks
        majorTicks.forEach(i => {
            angle = (((i - this.min) / (this.max - this.min)) * this.hasp_angle) + correctionAngle;
            if (i >= this.critical_value) {
                linecolor = majorTicksStyle.scale_end_color;
                linewidth = 4;
                // if (criticatStartAngle === 0) criticatStartAngle = angle;
            }
            this.drawTick(angle, halfWidth, halfWidth, majorInnerRadius, outerRadius, linewidth, linecolor);
            this.drawLabel(angle, halfWidth, halfWidth, innerRadius, this.style('knob').bg_color,
                formatGaugeLabel(i / this.div_format, this.decimals));
        });

        this.contentGroup.add(this.lineGroup);

        this.drawNeedles(halfWidth);
    }

    drawTick(angle, x, y, innerRadius, outerRadius, strokeWidth, linecolor) {
        if (typeof angle === 'number' && !isNaN(angle)) {
            let x1 = (Math.cos(angle * (Math.PI / 180)) * innerRadius);
            let y1 = (Math.sin(angle * (Math.PI / 180)) * innerRadius);
            let x2 = (Math.cos(angle * (Math.PI / 180)) * outerRadius);
            let y2 = (Math.sin(angle * (Math.PI / 180)) * outerRadius);
            const lineObj = new Konva.Line({
                x: x,
                y: y,
                points: [x1, y1, x2, y2],
                stroke: linecolor,
                obj: 'linemetergroup',
                strokeWidth: strokeWidth,
                tension: 0,
                listening: false,
            });
            this.lineGroup.add(lineObj);
            return lineObj;
        }
        return null;
    }

    drawLabel(angle, x, y, radius, color, text) {
        if (typeof angle === 'number' && !isNaN(angle)) {
            radius = radius - (this.text_font / 2);
            let lx1 = (Math.cos(angle * (Math.PI / 180)) * radius);
            let ly1 = (Math.sin(angle * (Math.PI / 180)) * radius);
            const textObj = new Konva.Text({
                x: x + lx1,
                y: y + ly1,
                obj: 'textobj',
                verticalAlign: 'middle',
                text: text,
                fontSize: this.text_font,
                fontFamily: this._scaleFontFamily,
                fill: this.style('main').text_color,
                listening: false,
                strokeWidth: 1,
                align: 'center',
            });

            // textObj.x(textObj.x() - (textObj.width() / 2))
            // Adjust text position to its center
            textObj.offsetX(textObj.width() / 2);
            textObj.offsetY(textObj.height() / 2);

            this.lineGroup.add(textObj);
            this.labels.push(textObj);
        }
    }

    drawCriticalArc(lineAngle, criticalangle, x, y, radius, color) {
        this.lineGroup.add(new CustomArc({
            id: uuidv4(),
            x: x,
            y: y,
            obj: "critical_arc",
            outerRadius: radius,
            innerRadius: radius - 5,
            startAngle: criticalangle,
            endAngle: criticalangle + lineAngle,
            // angle: lineAngle,
            // haspRotation: this.hasp_rotation,
            fill: color,
            // strokeWidth: 0,
        }));
    }

    drawScaleBorder(lineAngle, startAngle, x, y, radius, color) {
        const scaleBorderWidth = this.style('main').scale_border_width;
        if (lineAngle <= 0 || scaleBorderWidth <= 0) return;
        this.lineGroup.add(new CustomArc({
            id: uuidv4(),
            x: x,
            y: y,
            obj: 'scale_border',
            outerRadius: radius,
            innerRadius: radius - scaleBorderWidth,
            startAngle: startAngle,
            endAngle: startAngle + lineAngle,
            lineRounded: 'square',
            fill: color,
        }));
    }

    getMaxRadius10() {
        return this.centerRadius;
    }

    drawNeedles() {
        const indicator = this.style('indicator');
        const halfWidth = contentBox(this.style('main'), this.width(), this.height()).width / 2;
        const outerRadius = halfWidth * 0.85;

        let cap = 'square';
        if (indicator.line_rounded) {
            cap = 'round';
        }

        const plan = getGaugeNeedleRenderPlan(
            this,
            indicator.line_color,
            (datasetUUID) => this._resolveDataset(datasetUUID),
        );
        this.needleLines = plan.needles.map(({ angle, color }) => {
            const x1 = Math.cos(angle * (Math.PI / 180)) * 2;
            const y1 = Math.sin(angle * (Math.PI / 180)) * 2;
            const x2 = Math.cos(angle * (Math.PI / 180)) * outerRadius;
            const y2 = Math.sin(angle * (Math.PI / 180)) * outerRadius;
            const line = new Konva.Line({
                x: halfWidth,
                y: halfWidth,
                points: [x1, y1, x2, y2],
                stroke: color,
                obj: 'needle',
                strokeWidth: indicator.line_width,
                listening: false,
                lineCap: cap,
            });
            this.lineGroup.add(line);
            return line;
        });

        // LVGL draws one shared pivot after all needle lines.
        this.needleCenter = new Konva.Rect({
            id: uuidv4(),
            x: halfWidth - this.centerRadius,
            y: halfWidth - this.centerRadius,
            width: 2 * this.centerRadius,
            height: 2 * this.centerRadius,
            cornerRadius: indicator.radius,
            // radius: 15,
            stroke: indicator.border_color,
            strokeWidth: indicator.border_width,
            fill: indicator.bg_color,
            obj: "needle_center",
            listening: false,
        });
        this.lineGroup.add(this.needleCenter);
    }

    createText() {
        const halfWidth = this.width() / 2;
        const halfHeight = this.height() / 2;
        this.valueText = new Konva.Text({
            x: halfWidth,
            y: halfHeight,
            verticalAlign: 'middle',
            align: 'center',
            fontSize: this.value_font,
            fontFamily: 'Material Icons',
            text: processIconText(this.value_str),
            fill: this.value_color,
            obj: 'value_text',
            draggable: true,
            listening: true,
        });
        this.contentGroup.add(this.valueText);

        this.valueText.x((this.width() / 2) - (this.valueText.width() / 2) + (this.value_ofs_x || 0));
        this.valueText.y((this.height() / 2) - (this.valueText.height() / 2) + (this.value_ofs_y || 0));

        this.valueText.on('dragstart', (e) => {
            // console.log('drag start')
        });
        this.valueText.on('dragmove', (e) => {
            this.textBounds();
        });
        this.valueText.on('dragend', (e) => {
            this.textBounds();
        });
    }

    textBounds() {
        if (this.valueText !== undefined) {
            if (this.valueText.x() < 0) this.valueText.x(0);
            if (this.valueText.x() > this.width() - this.valueText.width()) this.valueText.x(this.width() - this.valueText.width());
            if (this.valueText.y() < 0) this.valueText.y(0);
            if (this.valueText.y() > this.height() - this.valueText.height()) this.valueText.y(this.height() - this.valueText.height());
        }
    }

    textPosition() {
        if (this.valueText !== undefined) {
            var scaledWidth = this.valueText.x() * this.scaledX;
            this.valueText.x(scaledWidth);
            const scaledHeight = this.valueText.y() * this.scaledY;
            this.valueText.y(scaledHeight);
            this.textBounds();
        }
    }

    redraw() {
        this.text_font = Number(this.text_font);
        if (!Array.isArray(this.needles)) this.val = Number(this.val);
        this.min = Number(this.min);
        this.max = Number(this.max);
        switch (this.format) {
            case 1:
                this.div_format = 10;
                break;
            case 2:
                this.div_format = 100;
                break;
            case 3:
                this.div_format = 1000;
                break;
            case 4:
                this.div_format = 10000;
                break;
            default:
                this.div_format = 1;
                break;
        }
        if (this.critical_value > this.max)
            this.critical_value = this.max;
        if (this.critical_value < this.min)
            this.critical_value = this.min;
        normalizeGaugeNeedleValues(this);
        this.drawLines();
    }

    /**
     * This widget needs the adjust becouse it fixes some min/max values in the propertiespanel
     * @param {*} type 
     * @param {*} value 
     */
    adjust(type, value) {
        if (!this._previewMutation) commitGaugeNeedlePreview(this);
        this._syncGaugeNeedleProperties();
        this.redraw();
        super.adjust(type, value);
    }

    objectExport(page, objectData, hassConfig) {
        super.objectExport(page, objectData, hassConfig);
        applyGaugeNeedlesExport(this.getConfiguredProperty('needles'), objectData);
        // Match Chart/Legend: the Designer keeps the UUID here and the
        // firmware exporter resolves it to the numeric dataset resource ID.
        if (this.dataset) objectData.dataset = this.dataset;
        if (this.value_str !== '') {
            objectData.value_ofs_x = this.valueText.x() + (this.valueText.width() / 2) - (this.width() / 2);
            objectData.value_ofs_y = this.valueText.y() + (this.valueText.height() / 2) - (this.height() / 2);
        }
    }

}

export function formatGaugeLabel(value, decimals) {
    return formatObjectDecimals(value, decimals);
}
